import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { AuthShell, field, label, primaryBtn, NEON } from "@/pages/Login";
import { Gift } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [f, setF] = useState({ name: "", org_name: "", email: "", password: "", gift_code: "", ref: "" });
  const [loading, setLoading] = useState(false);
  const [codeInfo, setCodeInfo] = useState(null);
  const [consent, setConsent] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  useEffect(() => {
    const ref = params.get("ref");
    if (ref) setF((p) => ({ ...p, ref }));
    const code = params.get("code");
    if (!code) return;
    setF((p) => ({ ...p, gift_code: code }));
    api.post(`/gift-codes/${encodeURIComponent(code)}/scan`)
      .then(({ data }) => setCodeInfo(data))
      .catch(() => {});
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) { toast.error("Devi accettare la Privacy Policy e i Termini per continuare"); return; }
    setLoading(true);
    try {
      await register({ ...f, privacy_accepted: true, service: params.get("service") || undefined });
      const months = codeInfo?.valid ? codeInfo.trial_months : null;
      toast.success(months ? `Account creato — ${months} mesi di prova attivati!` : "Account creato — prova gratuita attivata");
      navigate(params.get("service") === "airep" ? "/ai-report/app" : "/home");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registrazione non riuscita");
    } finally { setLoading(false); }
  };

  return (
    <AuthShell title="Crea il tuo account" subtitle="Crea il tuo account e accedi a tutti i servizi">
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div><label className={label}>Nome e cognome</label><input className={field} value={f.name} onChange={set("name")} required data-testid="register-name-input" /></div>
          <div><label className={label}>Nome azienda</label><input className={field} value={f.org_name} onChange={set("org_name")} required data-testid="register-org-input" placeholder="Es. Casa Rossi" /></div>
        </div>
        <div><label className={label}>Email</label><input className={field} type="email" value={f.email} onChange={set("email")} required data-testid="register-email-input" /></div>
        <div><label className={label}>Password</label><input className={field} type="password" value={f.password} onChange={set("password")} required minLength={6} data-testid="register-password-input" /></div>
        <div>
          <label className={label}>Codice omaggio evento (opzionale)</label>
          <div className="relative">
            <Gift className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input className={field + " pl-9 uppercase"} value={f.gift_code} onChange={set("gift_code")} placeholder="MPP-XXXXXXXX" data-testid="gift-code-input" />
          </div>
          {codeInfo?.valid ? (
            <p className="text-[11px] text-emerald-700 mt-1 flex items-center gap-1" data-testid="gift-code-valid-banner"><Gift className="h-3 w-3" /> {codeInfo.event_name}: {codeInfo.trial_months} mesi di prova gratuita!</p>
          ) : codeInfo?.expired ? (
            <p className="text-[11px] text-rose-600 mt-1" data-testid="gift-code-expired-banner">Codice scaduto: la promozione è terminata.</p>
          ) : (
            <p className="text-[11px] text-muted-foreground mt-1">Con un codice valido ottieni mesi di prova gratuita.</p>
          )}
        </div>
        <label className="flex items-start gap-3 rounded-xl p-4 cursor-pointer" style={{ backgroundColor: NEON }} data-testid="register-privacy-consent">
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 h-4 w-4 accent-neutral-900 shrink-0" data-testid="privacy-consent-checkbox" />
          <span className="text-sm text-neutral-900 leading-relaxed">Ho letto e accetto la <Link to="/privacy" target="_blank" className="font-bold underline">Privacy Policy</Link> e i <Link to="/termini" target="_blank" className="font-bold underline">Termini di Servizio</Link>, e acconsento al trattamento dei miei dati secondo il GDPR.</span>
        </label>
        <button className={primaryBtn} disabled={loading} data-testid="register-submit-button">{loading ? "Creazione..." : "Crea account"}</button>
      </form>
      <p className="text-sm text-muted-foreground mt-6 text-center">
        Hai già un account? <Link to="/login" className="text-primary font-medium hover:underline">Accedi</Link>
      </p>
    </AuthShell>
  );
}
